import { forwardRef } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Calendar } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import { useDatePickerContext } from "./DatePickerContext";
import { formatDateSmart } from "./formatDateSmart";
import type { DatePickerTriggerProps } from "./types";

const variantStyles = {
  input:
    "bg-card rounded-lg border border-border px-3 py-2 flex items-center gap-2 text-sm",
  ghost:
    "bg-transparent border border-transparent hover:border-border rounded-lg px-2 py-1 flex items-center gap-2 text-sm",
  icon: "p-2 rounded-lg hover:bg-card-hover inline-flex items-center justify-center",
};

const TriggerButton = forwardRef<
  HTMLButtonElement,
  DatePickerTriggerProps & React.ComponentPropsWithRef<"button">
>(function TriggerButton(
  { variant = "input", formatDate, className, ...props },
  ref,
) {
  const { selectedDate, isOpen } = useDatePickerContext();
  const { t } = useTranslation(["common"]);

  const displayText = selectedDate
    ? (formatDate?.(selectedDate) ?? formatDateSmart(selectedDate))
    : t("common:datePicker.selectDate");

  const openStyles = isOpen ? "ring-1 ring-accent/50" : "";

  return (
    <button
      ref={ref}
      type="button"
      className={`${variantStyles[variant]} ${openStyles} transition-colors cursor-pointer text-left ${className ?? ""}`}
      {...props}
    >
      <Calendar size={16} className="text-secondary shrink-0" />
      {variant !== "icon" && (
        <span className={selectedDate ? "text-primary" : "text-muted"}>
          {displayText}
        </span>
      )}
    </button>
  );
});

export function DatePickerTrigger({
  variant = "input",
  formatDate,
  className,
}: DatePickerTriggerProps) {
  return (
    <Popover.Trigger asChild>
      <TriggerButton
        variant={variant}
        formatDate={formatDate}
        className={className}
      />
    </Popover.Trigger>
  );
}
